import axios from 'axios';
import { useMaintenanceDialog } from 'common/MaintenanceProvider';
import useAuth from 'common/useAuth';
import React, { useCallback, useEffect } from 'react';

import {
  notificationHubClient,
  protectedApiClient,
  protectedApiClientV2
} from './support/baseApiClients';
import { MISSING_TOKEN_ERROR_KEY } from './support/createProtectedApiClient';
import { isMaintenanceResponse } from './support/isMaintenanceResponse';
import { refreshToken, repeatWhenPossible } from './support/tokenRefresher';
import { apiClient } from './support/unprotectedBaseApiClients';

interface Props {
  children: React.ReactNode;
}

const AxiosProvider: React.FC<Props> = (props): React.ReactElement => {
  const { children } = props;

  const { handleMissingToken, setUser } = useAuth();
  const { showMaintenanceDialog } = useMaintenanceDialog();

  const handleResponseError = useCallback(
    async (error: any) => {
      const originalRequest = error?.config;

      // support both Error objects and string rejections from request interceptor
      const isMissingTokenError =
        error === MISSING_TOKEN_ERROR_KEY ||
        (error && typeof error.message === 'string' && error.message === MISSING_TOKEN_ERROR_KEY);

      if (isMissingTokenError) {
        return handleMissingToken(error);
      }

      if (isMaintenanceResponse(error?.response?.status, error?.response?.data)) {
        showMaintenanceDialog(error.response.data);
        throw error;
      }

      if (error.response && error.response.status === 401) {
        try {
          const result = await refreshToken();
          result?.user && setUser(result.user);
        } catch (e) {
          // Only trigger logout for actual auth failures, not network errors
          const isNetworkError =
            axios.isAxiosError(error) &&
            (!error.response ||
              error.code === 'ECONNABORTED' ||
              error.code === 'ERR_NETWORK' ||
              error.code === 'ERR_CANCELED');

          if (!isNetworkError) {
            handleMissingToken(e);
          }
          // Re-throw so the original request fails with the network error
          throw e;
        }

        // All request (including first one) are queued to be executed when we get new token
        return repeatWhenPossible(originalRequest);
      }

      throw error;
    },
    [handleMissingToken, setUser, showMaintenanceDialog]
  );

  useEffect(() => {
    const protectedInterceptor = protectedApiClient.interceptors.response.use(
      (response) => response,
      handleResponseError
    );

    const protectedV2Interceptor = protectedApiClientV2.interceptors.response.use(
      (response) => response,
      handleResponseError
    );

    const notificationInterceptor = notificationHubClient.interceptors.response.use(
      (response) => response,
      handleResponseError
    );

    const publicInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      handleResponseError
    );

    return () => {
      protectedApiClient.interceptors.response.eject(protectedInterceptor);
      notificationHubClient.interceptors.response.eject(notificationInterceptor);
      protectedApiClientV2.interceptors.response.eject(protectedV2Interceptor);
      apiClient.interceptors.response.eject(publicInterceptor);
    };
  }, [handleResponseError]);

  return <>{children}</>;
};

export default AxiosProvider;
